/**
 * WaMediaSkeleton — placeholders type-specific para media en descarga.
 *
 * Patrón WhatsApp Web/iOS: cada tipo de media tiene su propio formato
 * visual mientras se descarga. NO usar spinner genérico:
 *   - Audio: skeleton waveform + mic icon + duración + spinner
 *   - Image: blurred placeholder con shimmer
 *   - Video: dark box con play icon + shimmer
 *   - Document: file icon + filename + size
 *   - Sticker: small box con shimmer
 *
 * Render condicional: si hay `media_thumbnail` (data URI base64), se muestra
 * el preview blurred. Si no, se muestra el skeleton genérico del tipo.
 */

import { Icon, Spinner } from '@/lib/polaris';
import { FileIcon, PlayIcon, MicrophoneIcon, ImageIcon } from '@/lib/polaris-icons';

interface Props {
  msgType: string;
  isOutbound: boolean;
  duration?: number | null;
  filename?: string | null;
  thumbnail?: string | null;
  isFailed: boolean;
  hasRetry?: boolean;
  onRetry?: () => void;
}

const formatDuration = (seconds: number | null | undefined): string => {
  if (!seconds || seconds <= 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

// Render barras de waveform con alturas variables para parecer audio real.
function WaveformBars({ outbound }: { outbound: boolean }) {
  const heights = [4, 8, 14, 10, 6, 12, 16, 8, 4, 10, 14, 6, 8, 12, 4, 10, 6, 14, 8, 4];
  const color = outbound ? 'rgba(0, 168, 132, 0.45)' : 'rgba(120, 120, 120, 0.45)';
  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2, height: 18 }}>
      {heights.map((h, i) => (
        <div key={i} style={{ width: 2, height: h, background: color, borderRadius: 1 }} />
      ))}
    </div>
  );
}

export function WaMediaSkeleton({
  msgType,
  isOutbound,
  duration,
  filename,
  thumbnail,
  isFailed,
  hasRetry,
  onRetry,
}: Props) {
  const errorIcon = (
    <span style={{ fontSize: 18, color: 'var(--p-color-text-warning)' }}>⚠️</span>
  );

  // ─── AUDIO / VOICE ─────────────────────────────────────────────────
  if (msgType === 'audio' || msgType === 'voice') {
    return (
      <div style={{
        width: 280,
        padding: '8px 10px 6px',
        background: 'var(--p-color-bg-surface-secondary)',
        borderRadius: 8,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        {/* Mic avatar (estilo WhatsApp iOS) */}
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: '#FF5252',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', flexShrink: 0,
        }}>
          <Icon source={MicrophoneIcon} tone="inherit" />
        </div>
        {/* Waveform skeleton */}
        <WaveformBars outbound={isOutbound} />
        {/* Spinner / retry / duration */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, fontSize: 11, color: 'var(--p-color-text-subdued)' }}>
          {isFailed ? (
            hasRetry && onRetry ? (
              <button onClick={onRetry} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: '#00a884', fontSize: 11 }}>
                ↻ Reintentar
              </button>
            ) : errorIcon
          ) : (
            <Spinner size="small" />
          )}
          <span>{formatDuration(duration)}</span>
        </div>
      </div>
    );
  }

  // ─── IMAGE ────────────────────────────────────────────────────────
  if (msgType === 'image') {
    // Si HAY thumbnail (jpegThumbnail base64): preview blurred (estilo
    // WhatsApp Web). Si NO hay thumbnail: shimmer gris animado + icon
    // de foto fade para que sea OBVIO que es una imagen cargando, no
    // un fondo vacío genérico.
    const SHIMMER_BG = 'linear-gradient(110deg, #d8dadc 8%, #e8eaed 18%, #d8dadc 33%)';
    return (
      <div style={{
        width: 290, height: 200,
        background: thumbnail ? '#333' : SHIMMER_BG,
        backgroundSize: thumbnail ? 'cover' : '200% 100%',
        backgroundImage: thumbnail ? `url("${thumbnail}")` : SHIMMER_BG,
        backgroundPosition: 'center',
        borderRadius: 6,
        position: 'relative', overflow: 'hidden',
        animation: thumbnail ? undefined : 'wa-shimmer 1.6s linear infinite',
      }}>
        {/* Blur overlay si hay thumbnail */}
        {thumbnail && (
          <div style={{ position: 'absolute', inset: 0, backdropFilter: 'blur(12px)' }} />
        )}
        {/* Foto icon fade si NO hay thumbnail — da contexto de que es una imagen */}
        {!thumbnail && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: 0.18,
          }}>
            <span style={{ transform: 'scale(4)', color: '#5c6670' }}>
              <Icon source={ImageIcon} />
            </span>
          </div>
        )}
        {/* Center indicator */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {isFailed ? (
            <div style={{
              background: 'rgba(0,0,0,0.6)', borderRadius: '50%',
              width: 56, height: 56,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: hasRetry ? 'pointer' : 'default',
            }} onClick={hasRetry ? onRetry : undefined}>
              {errorIcon}
            </div>
          ) : (
            <div style={{
              background: 'rgba(0,0,0,0.5)', borderRadius: '50%',
              width: 56, height: 56,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white',
            }}>
              <Spinner size="small" />
            </div>
          )}
        </div>
      </div>
    );
  }

  // ─── VIDEO ────────────────────────────────────────────────────────
  if (msgType === 'video') {
    const VIDEO_SHIMMER = 'linear-gradient(110deg, #2a2a2a 8%, #3a3a3a 18%, #2a2a2a 33%)';
    return (
      <div style={{
        width: 290, height: 200,
        background: thumbnail ? '#000' : VIDEO_SHIMMER,
        backgroundSize: thumbnail ? 'cover' : '200% 100%',
        backgroundImage: thumbnail ? `url("${thumbnail}")` : VIDEO_SHIMMER,
        backgroundPosition: 'center',
        borderRadius: 6,
        position: 'relative', overflow: 'hidden',
        animation: thumbnail ? undefined : 'wa-shimmer 1.6s linear infinite',
      }}>
        {thumbnail && (
          <div style={{ position: 'absolute', inset: 0, backdropFilter: 'blur(8px)', background: 'rgba(0,0,0,0.4)' }} />
        )}
        {/* Play button center */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', position: 'relative',
          }}>
            {isFailed ? (
              hasRetry ? (
                <button onClick={onRetry} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'white' }}>
                  ↻
                </button>
              ) : errorIcon
            ) : (
              <>
                <Icon source={PlayIcon} tone="inherit" />
                <div style={{ position: 'absolute', bottom: -28, fontSize: 11, color: 'white', whiteSpace: 'nowrap' }}>
                  <Spinner size="small" />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ─── DOCUMENT ─────────────────────────────────────────────────────
  if (msgType === 'document') {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 14px', borderRadius: 8,
        background: 'var(--p-color-bg-surface-secondary)',
        border: '1px solid var(--p-color-border-subdued)',
        maxWidth: 280,
      }}>
        <span style={{ flexShrink: 0, color: 'var(--p-color-icon-subdued)' }}>
          <Icon source={FileIcon} />
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {filename ?? 'Documento'}
          </div>
          <div style={{ fontSize: 11, color: 'var(--p-color-text-subdued)' }}>
            {isFailed ? (hasRetry ? 'No se pudo cargar — clic para reintentar' : 'Documento no disponible') : 'Descargando…'}
          </div>
        </div>
        <div style={{ flexShrink: 0 }}>
          {isFailed ? (
            hasRetry ? (
              <button onClick={onRetry} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#00a884', fontSize: 18 }}>
                ↻
              </button>
            ) : errorIcon
          ) : (
            <Spinner size="small" />
          )}
        </div>
      </div>
    );
  }

  // ─── STICKER ─────────────────────────────────────────────────────
  if (msgType === 'sticker') {
    return (
      <div style={{
        width: 120, height: 120,
        background: 'var(--p-color-bg-surface-secondary)',
        borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        animation: 'wa-pulse 1.4s ease-in-out infinite',
      }}>
        {isFailed ? errorIcon : <Spinner size="small" />}
      </div>
    );
  }

  // ─── FALLBACK (otros tipos) ───────────────────────────────────────
  return (
    <div style={{
      padding: '12px 16px', borderRadius: 8,
      background: 'var(--p-color-bg-surface-secondary)',
      display: 'flex', alignItems: 'center', gap: 8,
      animation: 'wa-pulse 1.4s ease-in-out infinite',
    }}>
      {isFailed ? errorIcon : <Spinner size="small" />}
      <span style={{ fontSize: 12, color: 'var(--p-color-text-subdued)' }}>
        {isFailed ? 'No disponible' : 'Cargando…'}
      </span>
    </div>
  );
}
